/**
 * External Imports
 */
import { FC, useState } from 'react';
import { connect } from 'react-redux';

/**
 * Internal Imports
 */
import { Dropdown } from '../../../shared/dropdowns';
import { RootState } from '../../../../redux/store';
import { useGetDivisionsQuery } from '../../../../redux/apis/Config.api';

/**
 * Global Type Definition Imports
 */
import { Filter, IFacility } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import { InputHeading, TextDiv } from './AdminFilterSection.styles';

const userRoles = [
  { value: 'patientRounder', displayName: 'Patient Rounder' },
  { value: 'employeeRounder', displayName: 'Employee Rounder' },
  { value: 'validationRounder', displayName: 'Validation Rounder' },
  { value: 'aidetValidationRounder', displayName: 'AIDET Validation Rounder' },
  { value: 'bssrValidationRounder', displayName: 'BSSR Validation Rounder' },
  { value: 'csrnRounder', displayName: 'CSC Rounder' },
  { value: 'cnedRounder', displayName: 'CNEd Rounder' },
  { value: 'templateConfigurator', displayName: 'Configurator' },
  { value: 'userAdmin', displayName: 'User Admin' },
  { value: 'enterpriseAdmin', displayName: 'Enterprise Admin' }
];

const accessLevels = [
  { value: 'facility', displayName: 'Facility' },
  { value: 'division', displayName: 'Division' },
  { value: 'master', displayName: 'Corporate' }
];

const AdminFilterSection: FC<{
  adminUserTab: string;
  facilities: IFacility[];
  filterValues: Filter;
  handleDropdownChange: (val: string, name: string) => void;
}> = ({ filterValues, facilities, adminUserTab, handleDropdownChange }) => {
  const [openUserRole, setOpenUserRole] = useState(false);
  const [openAccessLevel, setOpenAccessLevel] = useState(false);
  const [openAccessPlace, setOpenAccessPlace] = useState(false);

  const {
    data: divisionsData
    // isError: isGetDivisionsError,
    // isFetching: isGetDivisionsFetching,
    // isLoading: isGetDivisionsLoading
  } = useGetDivisionsQuery(undefined, {
    refetchOnMountOrArgChange: 120
  });

  return (
    <div id="Admin-Filter-Section">
      <InputHeading>User Role</InputHeading>
      {adminUserTab === 'allUsers' ? (
        <Dropdown
          id="Admin-Filter-User-Role-Select"
          classes="ps-0"
          name="userRole"
          open={openUserRole}
          placeholder={
            (filterValues.userRole &&
              userRoles.find(role => role.value === filterValues.userRole)
                ?.displayName) ||
            'Select a Role'
          }
          setOpen={setOpenUserRole}
        >
          {userRoles.map(role => (
            <div
              key={role.value}
              aria-selected={filterValues.userRole === role.value}
              className={`dropdown-hover-item${
                filterValues.userRole === role.value ? ' selected' : ''
              }`}
              role="option"
              tabIndex={0}
              onClick={() => handleDropdownChange(role.value, 'userRole')}
              onKeyDown={() => handleDropdownChange(role.value, 'userRole')}
            >
              {role.displayName}
            </div>
          ))}
        </Dropdown>
      ) : adminUserTab === 'patientRounder' ? (
        <TextDiv>Patient Rounder</TextDiv>
      ) : adminUserTab === 'employeeRounder' ? (
        <TextDiv>Employee Rounder</TextDiv>
      ) : adminUserTab === 'validationRounder' ? (
        <TextDiv>Validation Rounder</TextDiv>
      ) : adminUserTab === 'aidetValidationRounder' ? (
        <TextDiv>AIDET® Validation Rounder</TextDiv>
      ) : adminUserTab === 'bssrValidationRounder' ? (
        <TextDiv>BSSR Validation Rounder</TextDiv>
      ) : adminUserTab === 'csrnRounder' ? (
        <TextDiv>CSC Rounder</TextDiv>
      ) : adminUserTab === 'cnedRounder' ? (
        <TextDiv>CNEd Rounder</TextDiv>
      ) : adminUserTab === 'templateConfigurator' ? (
        <TextDiv>Configurator</TextDiv>
      ) : adminUserTab === 'userAdmin' ? (
        <TextDiv>User Admin</TextDiv>
      ) : (
        <TextDiv>Enterprise Admin</TextDiv>
      )}

      <InputHeading>Access Level</InputHeading>
      <Dropdown
        id="Admin-Filter-Access-Level-Select"
        classes="ps-0"
        name="accessLevel"
        open={openAccessLevel}
        placeholder={
          (filterValues.accessLevel &&
            accessLevels.find(level => level.value === filterValues.accessLevel)
              ?.displayName) ||
          'Select an Access Level'
        }
        setOpen={setOpenAccessLevel}
      >
        {accessLevels.map(level => (
          <div
            key={level.value}
            aria-selected={filterValues.accessLevel === level.value}
            className={`dropdown-hover-item${
              filterValues.accessLevel === level.value ? ' selected' : ''
            }`}
            role="option"
            tabIndex={0}
            onClick={() => handleDropdownChange(level.value, 'accessLevel')}
            onKeyDown={() => handleDropdownChange(level.value, 'accessLevel')}
          >
            {level.displayName}
          </div>
        ))}
      </Dropdown>

      {filterValues.accessLevel === 'facility' ? (
        <>
          <InputHeading>Access Places</InputHeading>
          <Dropdown
            id="Admin-Filter-Facility-Access-Place-Select"
            classes="ps-0"
            name="accessPlace"
            open={openAccessPlace}
            placeholder={
              (filterValues.accessPlace &&
                facilities.find(f => f.facilityId === filterValues.accessPlace)
                  ?.value) ||
              'Select a Facility'
            }
            setOpen={setOpenAccessPlace}
          >
            {facilities &&
              facilities.map((facility: IFacility) => (
                <div
                  key={facility.id}
                  aria-selected={
                    filterValues.accessPlace === facility.facilityId
                  }
                  className={`dropdown-hover-item${
                    filterValues.accessPlace === facility.facilityId
                      ? ' selected'
                      : ''
                  }`}
                  role="option"
                  tabIndex={0}
                  onClick={() =>
                    handleDropdownChange(facility.facilityId, 'accessPlace')
                  }
                  onKeyDown={() =>
                    handleDropdownChange(facility.facilityId, 'accessPlace')
                  }
                >
                  {facility.value}
                </div>
              ))}
          </Dropdown>
        </>
      ) : filterValues.accessLevel === 'division' ? (
        <>
          <InputHeading>Access Places</InputHeading>
          <Dropdown
            id="Admin-Filter-Division-Access-Place-Select"
            classes="ps-0"
            name="accessPlace"
            open={openAccessPlace}
            placeholder={
              (filterValues.accessPlace &&
                divisionsData?.find(
                  (d: IFacility) => d.id === filterValues.accessPlace
                )?.value) ||
              'Select a Division'
            }
            setOpen={setOpenAccessPlace}
          >
            {divisionsData?.map((division: { id: string; value: string }) => (
              <div
                key={division.id}
                aria-selected={filterValues.accessPlace === division.id}
                className={`dropdown-hover-item${
                  filterValues.accessPlace === division.id ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => handleDropdownChange(division.id, 'accessPlace')}
                onKeyDown={() =>
                  handleDropdownChange(division.id, 'accessPlace')
                }
              >
                {division.value}
              </div>
            )) || []}
          </Dropdown>
        </>
      ) : null}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    adminUserTab: state.AdminReducer.adminUserTab,
    facilities: state.ConfigReducer.userAccessibleFacilities
  };
};

export default connect(mapReduxStateToProps)(AdminFilterSection);
